import { forwardRef } from 'react';
import { InputAdornment, InputProps } from '@mui/material';

import { Icons } from 'Icon';

import { StyledTextFieldIcon, TextField as StyledTextfield } from './TextField.styled';
import { TextFieldProps } from './TextField.types';
import { TextMask } from './TextMask';

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  ({ placeholder, ...props }: TextFieldProps = {}, ref) => {
    if (props.mask && !props.onChange) {
      throw Error(
        `You are trying to use the \`mask\` prop without an \`onChange\` prop in your TextField component which can causes input delays/bugs. To avoid this error please make the component controlled by passing a \`value\` prop and an \`onChange\` prop that updates the \`value\` prop. You do not need to handle formatting within your \`onChange\` function, just make sure the function updates the \`value\` you are passing so that the component rerenders. You can see a working example of it in the component library's storybook.`,
      );
    }

    let InputProps: InputProps = props.InputProps ?? {};
    let InputComponent = props.InputProps?.inputComponent;
    let inputProps = props.inputProps;
    if (props.mask) {
      InputComponent = TextMask as any;
      props.mask === 'Date' ? (placeholder = 'MM/DD/YYYY') : (placeholder = '(123) 456-7890');
      inputProps = {
        textmask: props.mask,
      };
    }
    if (props.icon && Icons[props.icon]) {
      InputProps = {
        ...props.InputProps,
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{
              height: '100%',
              padding: '19px 13px 19px 0',
              marginLeft: 0,
              backgroundColor: (theme) => theme.palette.common.white,
            }}
            {...props.iconProps}
          >
            {<StyledTextFieldIcon icon={Icons[props.icon]} />}
          </InputAdornment>
        ),
        style: {
          paddingRight: '0',
        },
      };
    }

    if (props.disabled) {
      InputProps = {
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{
              height: '100%',
              padding: '19px 13px 19px 0',
              marginLeft: 0,
              backgroundColor: (theme) => theme.palette.secondary.main,
            }}
          >
            <StyledTextFieldIcon icon={Icons.lockSolid} />
          </InputAdornment>
        ),
        style: {
          paddingRight: '0',
        },
      };
    }

    return (
      <StyledTextfield
        ref={ref}
        {...props}
        InputLabelProps={
          props.hiddenLabel ? { ...props.InputLabelProps, className: 'sr-only' } : props.InputLabelProps
        }
        InputProps={{
          ...InputProps,
          inputComponent: InputComponent,
        }}
        inputProps={inputProps}
        placeholder={placeholder}
      />
    );
  },
);
TextField.displayName = 'TextField';

export { TextFieldProps };
